import React from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { Dialog, FCWithFragments, MoreMenuButton } from '@modernloop/shared/components';
import { getLocalTimezone } from '@modernloop/shared/datetime';
import { getExternalErrorMessage } from '@modernloop/shared/utils';
import { Box, Divider, MenuItem, MenuItemProps, Paper, Stack, Theme, Tooltip, Typography } from '@mui/material';
import { isAfter } from 'date-fns';
import { format as formatTz } from 'date-fns-tz';
import { useSnackbar } from 'notistack';

import {
  RescheduleLogModal_RescheduleLogFragment,
  RescheduleLogsList_RescheduleLogsFragment,
  useRescheduleLogDeleteMutation,
} from 'src/generated/mloop-graphql';

import TrashIllustration from 'src/assets/images/illustrations/trash_64.png';

import DurationLabel from 'src/components/DurationLabel';

import ActivityLogText from 'src/entities/ActivityLog/Helpers/ActivityLogText';

import { useActivityFeedRefetch } from 'src/hooks/useActivityFeedRefetch';
import useRefetchScheduleTaskQuery from 'src/hooks/useRefetchScheduleTaskQuery';

import { assertIsoTimestamp } from 'src/types/IsoTimestamp';

import RescheduleLogModal from './RescheduleLogModal';

interface Props {
  onModalClose: (val: boolean) => void;
  onRescheduleLogSelect: (rescheduleLog: RescheduleLogModal_RescheduleLogFragment) => void;
}

interface Fragments {
  // eslint-disable-next-line modernloop/component-with-fragments.cjs
  rescheduleLogs?: RescheduleLogsList_RescheduleLogsFragment[];
}

enum RescheduleLogOption {
  EDIT = 'edit-reschedule-log',
  DELETE = 'delete-reschedule-log',
}

const RescheduleLogsList: FCWithFragments<Fragments, Props> = ({
  rescheduleLogs,
  onModalClose,
  onRescheduleLogSelect,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [rescheduleLogId, setRescheduleLogId] = React.useState();
  const refetchactivityFeed = useActivityFeedRefetch();
  const refetchScheduleTaskQuery = useRefetchScheduleTaskQuery();
  const [rescheduleLogDeleteMutation, { loading: deleting }] = useRescheduleLogDeleteMutation();
  const options = [
    {
      id: RescheduleLogOption.EDIT,
      value: t('hiring-dashboard-tasks:tasks.rescheduleLog.edit'),
    },
    {
      id: RescheduleLogOption.DELETE,
      value: t('hiring-dashboard-common:common.delete'),
    },
  ];

  const onClose = () => {
    setShowDeleteDialog(false);
  };

  const handleDeleteRescheduleLog = () => {
    rescheduleLogDeleteMutation({
      variables: {
        input: {
          id: rescheduleLogId,
        },
      },
    }) // eslint-disable-next-line promise/always-return
      .then(() => {
        setShowDeleteDialog(false);
        refetchScheduleTaskQuery();
        refetchactivityFeed();
        enqueueSnackbar(t('hiring-dashboard-tasks:tasks.rescheduleLog.deletedSuccessfully'), { variant: 'success' });
      })
      .catch((error) => {
        enqueueSnackbar(getExternalErrorMessage(error), { variant: 'error' });
      });
  };

  function handleMenuOptionSelect(option: MenuItemProps, item) {
    if (option.id === RescheduleLogOption.EDIT) {
      onRescheduleLogSelect(item);
      onModalClose(true);
    } else if (option.id === RescheduleLogOption.DELETE) {
      setRescheduleLogId(item.id);
      setShowDeleteDialog(true);
    }
  }

  return (
    <>
      <Stack direction="column" spacing={1}>
        {rescheduleLogs?.map((item) => {
          const isEditedTooltip = (
            <Typography variant="body2" color="text.secondary">
              {t('hiring-dashboard-tasks:tasks.rescheduleLog.edited')}{' '}
              <DurationLabel
                timestamp={assertIsoTimestamp(item?.updatedAt as string)}
                typographyProps={{
                  variant: 'body2',
                  fontWeight: 400,
                  color: 'text.secondary',
                  noWrap: true,
                  component: 'span',
                }}
              />{' '}
              {t('hiring-dashboard-tasks:tasks.rescheduleLog.byName', { name: item?.lastUpdatedBy?.fullName || '' })}
            </Typography>
          );
          const isEdited = isAfter(new Date(item?.updatedAt), new Date(item?.createdAt)) && (
            <Tooltip title={isEditedTooltip}>
              <Typography variant="body2" color="text.secondary">
                &nbsp;&#x2022;&nbsp;{t('hiring-dashboard-tasks:tasks.rescheduleLog.edited')}
              </Typography>
            </Tooltip>
          );

          return (
            <Paper
              key={item.id}
              sx={{
                padding: (theme: Theme) => theme.spacing(2),
                border: (theme: Theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Stack spacing={1}>
                <Stack direction="row" alignItems="left" justifyContent="space-between">
                  <Stack direction="column" alignItems="left" justifyContent="space-between">
                    <Typography
                      variant="subtitle1"
                      sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
                    >
                      {item?.rescheduleReason?.reason}
                    </Typography>
                    <Stack direction="row" alignItems="left" justifyContent="left">
                      <Typography variant="body2" color="text.secondary">
                        {t('hiring-dashboard-tasks:tasks.rescheduleLog.rescheduledOn', {
                          date: formatTz(new Date(item?.rescheduledAt), 'MMM d, yyyy', {
                            timeZone: getLocalTimezone(),
                          }),
                        })}
                        &nbsp;
                        <ActivityLogText fontWeight={600} color="foreground" noWrap>
                          {item?.performedBy?.fullName}
                        </ActivityLogText>
                      </Typography>
                      {isEdited}
                    </Stack>
                  </Stack>
                  <Box>
                    <MoreMenuButton
                      iconSx={{
                        height: '24px',
                        width: '32px',
                        borderRadius: '4px',
                      }}
                      dataTestId="edit-reschedule-log-button"
                    >
                      {options.map((option) => (
                        <MenuItem key={option.id} onClick={() => handleMenuOptionSelect(option, item)} id={option.id}>
                          {option.value}
                        </MenuItem>
                      ))}
                    </MoreMenuButton>
                  </Box>
                </Stack>
                {item?.note && (
                  <>
                    <Divider />
                    <Typography variant="body2" color="text.secondary">
                      {item?.note}
                    </Typography>
                  </>
                )}
              </Stack>
            </Paper>
          );
        })}
      </Stack>
      {showDeleteDialog && (
        <Dialog
          open
          onClose={() => setShowDeleteDialog(false)}
          title={t('hiring-dashboard-tasks:tasks.rescheduleLog.deleteTitle')}
          subTitle={t('hiring-dashboard-tasks:tasks.rescheduleLog.deleteSubtitle')}
          decoration={
            <img
              style={{ width: '64px', height: '64px' }}
              src={TrashIllustration}
              alt={t('hiring-dashboard-tasks:tasks.rescheduleLog.deleteAlt')}
            />
          }
          cancelOptions={{
            onClick: onClose,
          }}
          submitOptions={{
            label: t('hiring-dashboard-common:common.delete'),
            isDangerous: true,
            isLoading: deleting,
            isDisabled: deleting,
            onClick: handleDeleteRescheduleLog,
          }}
        >
          <></>
        </Dialog>
      )}
    </>
  );
};

RescheduleLogsList.fragments = {
  rescheduleLogs: gql`
    ${RescheduleLogModal.fragments.rescheduleLog}
    fragment RescheduleLogsList_rescheduleLogs on RescheduleLog {
      id
      ...RescheduleLogModal_rescheduleLog
    }
  `,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const RescheduleLogDeleteMutation = gql`
  mutation RescheduleLogDelete($input: RescheduleLogDeleteInput!) {
    rescheduleLogDelete(input: $input) {
      result
    }
  }
`;

export default RescheduleLogsList;
